import React, { useState, useEffect } from "react";
import { Box, Layer, Button, Text, Notification } from "grommet";

/**
 * Utility function to purge bookmarks older than 12 hours
 */
const purgeOldBookmarks = (bookmarksArray) => {
  const now = Date.now();
  const twelveHoursInMs = 12 * 60 * 60 * 1000;
  return bookmarksArray.filter(
    (bookmark) => now - bookmark.timestamp < twelveHoursInMs
  );
};

/**
 * Utility function to bookmark a pod session
 */
export const bookmarkPodSession = (podSession) => {
  if (!podSession || !podSession.id || !podSession.testerName) {
    return false;
  }

  const storedBookmarks = localStorage.getItem("meduPodBookmarks");
  let bookmarksArray = [];

  if (storedBookmarks) {
    try {
      bookmarksArray = JSON.parse(storedBookmarks);
    } catch (error) {
      console.error("Error parsing existing bookmarks:", error);
      bookmarksArray = [];
    }
  }

  // Purge old bookmarks
  bookmarksArray = purgeOldBookmarks(bookmarksArray);

  // Check if this session is already bookmarked
  const existingIndex = bookmarksArray.findIndex(
    (bookmark) => bookmark.id === podSession.id
  );

  const newBookmark = {
    id: podSession.id,
    testerName: podSession.testerName,
    organizationName: podSession.organizationName || "Unknown Organization",
    timestamp: Date.now(),
  };

  if (existingIndex >= 0) {
    bookmarksArray[existingIndex] = newBookmark;
  } else {
    bookmarksArray.push(newBookmark);
  }

  localStorage.setItem("meduPodBookmarks", JSON.stringify(bookmarksArray));
  return true;
};

/**
 * Utility function to get all bookmarks
 */
export const getBookmarks = () => {
  const storedBookmarks = localStorage.getItem("meduPodBookmarks");
  if (!storedBookmarks) return [];

  try {
    const bookmarksArray = JSON.parse(storedBookmarks);
    const purged = purgeOldBookmarks(bookmarksArray);
    // Update localStorage with purged bookmarks
    if (purged.length !== bookmarksArray.length) {
      localStorage.setItem("meduPodBookmarks", JSON.stringify(purged));
    }
    return purged;
  } catch (error) {
    console.error("Error parsing bookmarks:", error);
    return [];
  }
};

/**
 * PodBookmarksViewer Component
 *
 * A component that displays bookmarked sessions in a layer/modal.
 * Can be used independently or controlled by parent component.
 */
export const PodBookmarksViewer = ({ onClose, isOpen }) => {
  const [bookmarks, setBookmarks] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const bookmarksArray = getBookmarks();
      setBookmarks(bookmarksArray);
    }
  }, [isOpen]);

  const handleOpenSession = (sessionId) => {
    window.open(`/pod-session?id=${sessionId}`, "_blank");
  };

  if (!isOpen) return null;

  return (
    <Layer position="center" modal onClickOutside={onClose} onEsc={onClose}>
      <Box pad="small" width="large" gap="medium">
        <Text size="large" weight="bold">
          Bookmarked Sessions
        </Text>
        <Text size="small" color="dark-4">
          Scroll to view more
        </Text>
        <Box overflow="auto" height="medium" pad={{ horizontal: "small" }}>
          {bookmarks.length === 0 ? (
            <Text>No bookmarked sessions found.</Text>
          ) : (
            <Box gap="small">
              {bookmarks.map((bookmark) => (
                <Box
                  key={bookmark.id}
                  pad="medium"
                  direction="row"
                  justify="between"
                  align="center"
                  margin={{ bottom: "small" }}
                  border={{ side: "bottom" }}
                >
                  <Box>
                    <Text weight="bold">{bookmark.testerName}</Text>
                    <Text size="small" color="dark-4">
                      {bookmark.organizationName}
                    </Text>
                  </Box>
                  <Button
                    label="Open Session"
                    primary
                    onClick={() => handleOpenSession(bookmark.id)}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <Button label="Close" onClick={onClose} margin={{ top: "small" }} />
      </Box>
    </Layer>
  );
};

/**
 * PodBookmarkNotification Component
 *
 * A component that shows a toast notification when a session is bookmarked.
 */
export const PodBookmarkNotification = ({ show, onClose }) => {
  if (!show) return null;

  return (
    <Notification
      toast
      title="Success"
      status="normal"
      message="Session bookmarked successfully!"
      onClose={onClose}
    />
  );
};

/**
 * usePodBookmarks Hook
 *
 * A custom hook for managing pod session bookmarks.
 * Returns functions to bookmark and view bookmarks, plus state for notifications.
 */
export const usePodBookmarks = (podSession) => {
  const [showNotification, setShowNotification] = useState(false);
  const [showViewer, setShowViewer] = useState(false);

  const bookmarkSession = () => {
    if (bookmarkPodSession(podSession)) {
      setShowNotification(true);
    }
  };

  const viewBookmarks = () => {
    setShowViewer(true);
  };

  return {
    bookmarkSession,
    viewBookmarks,
    showNotification,
    setShowNotification,
    showViewer,
    setShowViewer,
  };
};

export default PodBookmarksViewer;
